import { useRef, useEffect, useState } from "react";
import { Cake, Briefcase, PartyPopper, Heart, ArrowUpRight } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.15 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, [ref]);
  return inView;
}

// Image for Activities section
const activitiesImage = "/assets/img/atividades.webp";

const activities = [
  { icon: Cake, title: "Festas de Aniversário", desc: "Pacotes com campo, lanche e diversão." },
  { icon: Briefcase, title: "Team Buildings", desc: "Torneios corporativos para equipas." },
  { icon: PartyPopper, title: "Convívios", desc: "Campos + bar para momentos com amigos." },
  { icon: Heart, title: "Eventos Especiais", desc: "Despedidas e celebrações memoráveis." },
];

export default function Activities() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section id="activities" data-testid="activities-section" ref={ref} className="py-24 bg-[#0a0f1a]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`${inView ? 'fade-in-up' : 'opacity-0'}`}>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Title */}
            <div className="text-center mb-8">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Atividades</span>
              <h2 className="font-display text-3xl text-white font-medium leading-tight">
                Muito mais que padel.
              </h2>
            </div>

            {/* Image Mobile */}
            <div className="relative w-full mb-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 w-full">
                <div className="aspect-[9/16]">
                  <img
                    src={activitiesImage}
                    alt="Atividade"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="activities-image-mobile"
                  />
                </div>
              </div>
            </div>

            {/* Activities Grid */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {activities.map((a, i) => (
                <div key={a.title} className="bg-[#0d1320] border border-white/5 rounded-xl p-5 hover:border-jp-lime/15 transition-colors group" data-testid={`activity-card-mobile-${i}`}>
                  <a.icon className="w-6 h-6 mb-3 text-jp-lime group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  <h3 className="font-body font-medium text-white text-base mb-1">{a.title}</h3>
                  <p className="font-body text-sm text-white/40 leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <a
              href="#contacts"
              className="w-full bg-jp-lime text-black font-body font-semibold text-sm px-6 py-4 rounded-full hover:bg-jp-lime-dark transition-all flex items-center justify-center gap-2 group"
              data-testid="activities-cta-mobile"
            >
              Saber mais
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>

          {/* Desktop Layout: Content left, Image right */}
          <div className="hidden lg:block">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Content on the left */}
              <div className="lg:col-span-7">
                <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Atividades</span>
                <h2 className="font-display text-4xl sm:text-5xl text-white font-medium leading-tight mb-8">
                  Muito mais que padel.
                </h2>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {activities.map((a, i) => (
                    <div key={a.title} className="bg-[#0d1320] border border-white/5 rounded-xl p-6 hover:border-jp-lime/15 transition-colors group" data-testid={`activity-card-${i}`}>
                      <a.icon className="w-7 h-7 mb-4 text-jp-lime group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                      <h3 className="font-body font-medium text-white text-lg mb-2">{a.title}</h3>
                      <p className="font-body text-base text-white/40 leading-relaxed">{a.desc}</p>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <a
                  href="#contacts"
                  className="inline-flex bg-jp-lime text-black font-body font-semibold text-sm px-8 py-4 rounded-full hover:bg-jp-lime-dark transition-all items-center gap-2 group"
                  data-testid="activities-cta"
                >
                  Saber mais e reservar
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>

              {/* Image on the right */}
              <div className="lg:col-span-5">
                <div className="relative flex justify-end">
                  <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-[320px]">
                    <div className="aspect-[9/16]">
                      <img
                        src={activitiesImage}
                        alt="Atividade"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        data-testid="activities-image"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
